import axios from "axios";

const origin = String(process.env.REACT_APP_PMS_API_BASE_URL || "").replace(/\/+$/, "");
export const API_PREFIX = "/api/v1";
export const API_BASE_URL = `${origin}${API_PREFIX}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: "application/json", "Content-Type": "application/json" },
  paramsSerializer: { indexes: null },
});

const SESSION_KEYS = [
  "access_token", "user", "pms_role", "pms_username", "pms_active_view",
  "permissions", "roles", "lastRoute",
];

export function clearStoredSession() {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

// [NEW] Stop calling the backend with a login that is already over.
// Before: when the 60-minute backend session ended, the keep-alive check, the 30-second notification refresh and every
// page kept sending the old token, and the backend recorded each one as "REQUEST_REJECTED /auth/me SESSION_INVALID".
// Now: the first 401 marks that token as dead, and from then on (or as soon as the session's expiry time has passed)
// requests carrying it are stopped in the browser and the user is sent to the sign-in page once.
const SESSION_EXPIRES_KEY = "pms_session_expires_at";
let deadToken = null;
export const markSessionDead = (token) => { if (token) deadToken = token; };
export function sessionIsOver(token = localStorage.getItem("access_token")) {
  if (!token) return false;
  if (token === deadToken) return true;
  const expiresAt = Number(localStorage.getItem(SESSION_EXPIRES_KEY) || 0);
  return Boolean(expiresAt) && Date.now() >= expiresAt;
}
const localSessionError = (config) => {
  const error = new Error("Your session has expired. Please sign in again.");
  error.config = config;
  error.code = "SESSION_EXPIRED_LOCALLY";
  error.response = { status: 401, data: { error: { code: "SESSION_INVALID", message: error.message } }, config };
  return error;
};

const goToLogin = (reason) => {
  clearStoredSession();
  if (reason) sessionStorage.setItem("pms_login_notice", reason);
  if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  const url = config.url || "";
  const isPublic = PUBLIC_AUTH_CALLS.some((path) => url.startsWith(path)) || url.startsWith("/auth/entra") || url.startsWith("/auth/logout");
  if (token && !isPublic && sessionIsOver(token)) {
    markSessionDead(token);
    if (!config.skipAuthRedirect) goToLogin("Your session has expired. Please sign in again.");
    return Promise.reject(localSessionError(config));
  }
  config.headers = config.headers || {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.params) {
    config.params = Object.fromEntries(
      Object.entries(config.params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
  }
  return config;
});

const PUBLIC_AUTH_CALLS = [
  "/auth/login",
  // ===== OTP DISABLED (temporary) - START =====
  // "/auth/verify-otp",
  // "/auth/resend-otp",
  // ===== OTP DISABLED (temporary) - END =====
  "/auth/change-password",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/accept-invitation",
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.error?.code;
    const url = error.config?.url || "";
    const isAuthCall = PUBLIC_AUTH_CALLS.some((path) => url.startsWith(path));
    const headers = error.config?.headers;
    const sentAuth = String(
      (typeof headers?.get === "function" ? headers.get("Authorization") : null) ||
      headers?.Authorization || headers?.authorization || ""
    );
    const currentToken = localStorage.getItem("access_token");
    const isCurrentSession = Boolean(currentToken) && sentAuth === `Bearer ${currentToken}`;
    const skipRedirect = Boolean(error.config?.skipAuthRedirect);
    if (!isAuthCall && status === 401 && isCurrentSession && error.code !== "SESSION_EXPIRED_LOCALLY") markSessionDead(currentToken);
    if (!isAuthCall && !skipRedirect && status === 401 && isCurrentSession && error.code !== "SESSION_EXPIRED_LOCALLY") {
      goToLogin("Your session has expired. Please sign in again.");
    } else if (!isAuthCall && status === 403 && code === "PASSWORD_CHANGE_REQUIRED" && isCurrentSession) {
      goToLogin("Change your temporary password to continue.");
    }
    return Promise.reject(error);
  }
);

export default api;
